import type { OrgRole } from '@/types/roles';

export type PermissionEffect = 'GRANT' | 'DENY';

export const PERMISSION_CATEGORIES = [
  {
    key: 'jobs',
    label: 'Jobs',
    permissions: [
      { key: 'jobs.view', label: 'View jobs' },
      { key: 'jobs.create', label: 'Create jobs' },
      { key: 'jobs.update', label: 'Edit jobs' },
      { key: 'jobs.delete', label: 'Delete jobs', sensitive: true },
      { key: 'jobs.schedule.manage', label: 'Manage job schedules' },
      { key: 'jobs.billing.manage', label: 'Manage job billing', sensitive: true },
    ],
  },
  {
    key: 'shifts',
    label: 'Shifts',
    permissions: [
      { key: 'shifts.view', label: 'View shifts' },
      { key: 'shifts.view_all', label: 'View all organisation shifts', sensitive: true },
      { key: 'shifts.create', label: 'Create shifts' },
      { key: 'shifts.update', label: 'Edit shifts' },
      { key: 'shifts.cancel', label: 'Cancel shifts' },
      { key: 'shifts.delete', label: 'Delete shifts', sensitive: true },
      { key: 'shifts.assign', label: 'Assign employees' },
      { key: 'shifts.recurring.manage', label: 'Manage recurring series' },
      { key: 'shifts.csv_import', label: 'Import shifts from CSV' },
    ],
  },
  {
    key: 'payroll',
    label: 'Pay and Timesheets',
    permissions: [
      { key: 'shift_pay.view', label: 'View shift pay', sensitive: true },
      { key: 'shift_pay.manage', label: 'Edit shift pay', sensitive: true },
      { key: 'timesheets.view', label: 'View timesheets' },
      { key: 'timesheets.approve', label: 'Approve timesheets' },
      { key: 'timesheets.reject', label: 'Reject timesheets' },
      { key: 'payroll.view', label: 'View payroll', sensitive: true },
      { key: 'payroll.manage', label: 'Manage payroll setup', sensitive: true },
      { key: 'payroll.export', label: 'Export payroll', sensitive: true },
    ],
  },
  {
    key: 'leave',
    label: 'Leave and Coverage',
    permissions: [
      { key: 'leave.view_team', label: 'View team leave' },
      { key: 'leave.approve', label: 'Approve leave' },
      { key: 'leave.escalate', label: 'Escalate leave' },
      { key: 'leave.coverage.manage', label: 'Manage leave coverage' },
      { key: 'leave.policy.manage', label: 'Manage leave policy', sensitive: true },
    ],
  },
  {
    key: 'billing',
    label: 'Invoices and Billing',
    permissions: [
      { key: 'invoices.view', label: 'View invoices', sensitive: true },
      { key: 'invoices.issue', label: 'Issue invoices', sensitive: true },
      { key: 'invoices.send', label: 'Send invoices', sensitive: true },
      { key: 'invoices.mark_paid', label: 'Mark invoices paid', sensitive: true },
      { key: 'invoices.void', label: 'Void invoices', sensitive: true },
      { key: 'billing_adjustments.manage', label: 'Manage billing extras', sensitive: true },
    ],
  },
  {
    key: 'team',
    label: 'Team',
    permissions: [
      { key: 'team.view', label: 'View team' },
      { key: 'team.invite_employee', label: 'Invite employees' },
      { key: 'team.invite_manager', label: 'Invite managers', sensitive: true },
      { key: 'team.invite_bm', label: 'Invite business managers', sensitive: true },
      { key: 'team.update_roles', label: 'Change member roles', sensitive: true },
      { key: 'team.deactivate', label: 'Deactivate members', sensitive: true },
    ],
  },
  {
    key: 'settings',
    label: 'Settings and Compliance',
    permissions: [
      { key: 'public_holidays.manage', label: 'Manage public holidays' },
      { key: 'shift_rules.manage', label: 'Manage shift rules' },
      { key: 'roster_digest.manage', label: 'Manage weekly roster emails' },
      { key: 'organization_settings.manage', label: 'Manage organization settings', sensitive: true },
      { key: 'permissions.manage', label: 'Manage permissions', sensitive: true },
      { key: 'audit_log.view', label: 'View audit log', sensitive: true },
    ],
  },
  {
    key: 'reports',
    label: 'Reports',
    permissions: [
      { key: 'reports.view', label: 'View reports' },
      { key: 'reports.export', label: 'Export reports' },
      { key: 'reports.financial.view', label: 'View financial reports', sensitive: true },
      { key: 'custom_reports.view', label: 'View custom reports' },
    ],
  },
  {
    key: 'employee',
    label: 'Employee Self Service',
    permissions: [
      { key: 'shifts.own.view', label: 'View own shifts' },
      { key: 'shift_clock.manage', label: 'Clock in and out' },
      { key: 'job_photos.manage', label: 'Upload job photos' },
      { key: 'leave.own.view', label: 'View own leave' },
      { key: 'leave.request', label: 'Request leave' },
      { key: 'timesheets.own.view', label: 'View own timesheets' },
      { key: 'timesheets.own.submit', label: 'Submit own timesheets' },
      { key: 'availability.manage', label: 'Manage own availability' },
      { key: 'profile.manage', label: 'Manage own profile' },
    ],
  },
] as const;

export const CORE_PERMISSION_DEFINITIONS = [
  { key: 'shifts.own.view', label: 'View own shifts' },
  { key: 'shift_clock.manage', label: 'Clock in and out' },
  { key: 'job_photos.manage', label: 'Upload job photos' },
  { key: 'leave.own.view', label: 'View own leave' },
  { key: 'leave.request', label: 'Request leave' },
  { key: 'timesheets.own.view', label: 'View own timesheets' },
  { key: 'timesheets.own.submit', label: 'Submit own timesheets' },
  { key: 'availability.manage', label: 'Manage own availability' },
  { key: 'profile.manage', label: 'Manage own profile' },
] as const;

export const ADJUSTABLE_PERMISSION_CATEGORIES = PERMISSION_CATEGORIES.filter(
  (category) => category.key !== 'employee',
) as Exclude<(typeof PERMISSION_CATEGORIES)[number], { key: 'employee' }>[];

export type PermissionCategory = (typeof PERMISSION_CATEGORIES)[number];
export type AdjustablePermissionCategory = (typeof ADJUSTABLE_PERMISSION_CATEGORIES)[number];
export type PermissionDefinition =
  | PermissionCategory['permissions'][number]
  | (typeof CORE_PERMISSION_DEFINITIONS)[number];
export type PermissionKey = PermissionDefinition['key'];
export type PermissionSource = 'default' | 'granted' | 'denied' | 'owner';

export interface PermissionOverrideLike {
  permission_key: string;
  effect: PermissionEffect;
}

export interface PermissionResolution {
  allowed: boolean;
  defaultAllowed: boolean;
  source: PermissionSource;
}

export const ALL_PERMISSION_KEYS = PERMISSION_CATEGORIES.flatMap((category) =>
  category.permissions.map((permission) => permission.key),
) as PermissionKey[];

export const CORE_PERMISSION_KEYS = CORE_PERMISSION_DEFINITIONS.map(
  (permission) => permission.key,
) as PermissionKey[];

/**
 * Where each role sits. Somebody can change another member's permissions only when they
 * rank strictly above them, so two owners cannot edit each other, nor two business
 * managers. Gaps are left so a role such as an accountant can sit between two others
 * without renumbering.
 */
export const ROLE_RANK = {
  BO: 100,
  BM: 80,
  MANAGER: 50,
  EMPLOYEE: 10,
} satisfies Record<OrgRole, number>;

/** Powers that stay with the owner, whatever anybody else is granted. */
export const OWNER_ONLY_PERMISSION_KEYS = [
  'organization_settings.manage',
  'team.invite_bm',
  'team.update_roles',
] as const satisfies readonly PermissionKey[];

/**
 * The switches an owner or business manager sees for a manager: defaults worth turning
 * off and extras worth turning on. With the manager defaults, this is also the most a
 * manager can hold. No pay, payroll, invoice or client billing figures: a manager
 * approves hours without seeing what they cost.
 */
const MANAGER_ADJUSTABLE_PERMISSION_KEYS = [
  'jobs.view',
  'jobs.create',
  'jobs.update',
  'jobs.schedule.manage',
  'shifts.view_all',
  'shifts.delete',
  'shifts.csv_import',
  'timesheets.approve',
  'timesheets.reject',
  'leave.view_team',
  'leave.approve',
  'leave.escalate',
  'leave.coverage.manage',
  'leave.policy.manage',
  'public_holidays.manage',
  'shift_rules.manage',
  'roster_digest.manage',
  'reports.view',
  'permissions.manage',
] as const satisfies readonly PermissionKey[];

/** Keys only offered to managers; a business manager sees every shift by default. */
const MANAGER_ONLY_PERMISSION_KEYS = new Set<PermissionKey>(['shifts.view_all']);

/**
 * Profile and availability belong to the person and travel with them between
 * organisations, so they are not switches an organisation gets.
 */
const PERSONAL_PERMISSION_KEYS = new Set<PermissionKey>(['profile.manage', 'availability.manage']);

const EMPLOYEE_PERMISSION_CATEGORIES = PERMISSION_CATEGORIES.filter(
  (category) => category.key === 'employee',
).map((category) => ({
  ...category,
  permissions: category.permissions.filter((permission) => !PERSONAL_PERMISSION_KEYS.has(permission.key)),
}));

export function getPermissionCategoriesForRole(role: OrgRole | null | undefined) {
  if (!role || role === 'BO') return [];
  // An employee's self-service, which their manager can switch off item by item.
  if (role === 'EMPLOYEE') return EMPLOYEE_PERMISSION_CATEGORIES;

  const offered: ReadonlySet<PermissionKey> =
    role === 'MANAGER'
      ? new Set<PermissionKey>(MANAGER_ADJUSTABLE_PERMISSION_KEYS)
      : new Set(
          ROLE_PERMISSION_CEILINGS[role].filter((key) => !MANAGER_ONLY_PERMISSION_KEYS.has(key)),
        );

  return ADJUSTABLE_PERMISSION_CATEGORIES.map((category) => ({
    ...category,
    permissions: category.permissions.filter((permission) => offered.has(permission.key)),
  })).filter((category) => category.permissions.length > 0);
}

const ROLE_DEFAULT_PERMISSION_KEYS = {
  BO: ALL_PERMISSION_KEYS,
  BM: [
    'jobs.view',
    'jobs.create',
    'jobs.update',
    'jobs.schedule.manage',
    'shifts.view',
    'shifts.create',
    'shifts.update',
    'shifts.cancel',
    'shifts.assign',
    'shifts.recurring.manage',
    'shifts.csv_import',
    'shift_pay.view',
    'timesheets.view',
    'timesheets.approve',
    'timesheets.reject',
    'leave.view_team',
    'leave.approve',
    'leave.escalate',
    'leave.coverage.manage',
    'leave.policy.manage',
    'invoices.view',
    'team.view',
    'team.invite_employee',
    'team.deactivate',
    'public_holidays.manage',
    'shift_rules.manage',
    'roster_digest.manage',
    'audit_log.view',
    'reports.view',
    'reports.export',
    'reports.financial.view',
    'custom_reports.view',
    // For the managers and employees below them; see ROLE_RANK.
    'permissions.manage',
    ...CORE_PERMISSION_KEYS,
  ],
  MANAGER: [
    'shifts.view',
    'shifts.create',
    'shifts.update',
    'shifts.cancel',
    'shifts.assign',
    'shifts.recurring.manage',
    'timesheets.view',
    'timesheets.approve',
    'timesheets.reject',
    'leave.view_team',
    'leave.approve',
    'leave.escalate',
    'leave.coverage.manage',
    'team.view',
    'reports.view',
    'custom_reports.view',
    // For the employees below them; see ROLE_RANK.
    'permissions.manage',
    ...CORE_PERMISSION_KEYS,
  ],
  EMPLOYEE: CORE_PERMISSION_KEYS,
} satisfies Record<OrgRole, readonly PermissionKey[]>;

/**
 * The most each role can ever hold. A grant beyond it is refused when saved and ignored if
 * one exists, so moving somebody to a lower role takes their extra access with it.
 *
 * The database holds the same matrix (private.role_permissions); a unit test compares them.
 */
export const ROLE_PERMISSION_CEILINGS = {
  BO: ALL_PERMISSION_KEYS,
  BM: ALL_PERMISSION_KEYS.filter(
    (key) => !(OWNER_ONLY_PERMISSION_KEYS as readonly PermissionKey[]).includes(key),
  ),
  MANAGER: [
    ...new Set<PermissionKey>([
      ...ROLE_DEFAULT_PERMISSION_KEYS.MANAGER,
      ...MANAGER_ADJUSTABLE_PERMISSION_KEYS,
    ]),
  ],
  EMPLOYEE: CORE_PERMISSION_KEYS,
} satisfies Record<OrgRole, readonly PermissionKey[]>;

export function roleCeilingAllows(
  role: OrgRole | null | undefined,
  permissionKey: PermissionKey,
): boolean {
  if (!role) return false;
  return (ROLE_PERMISSION_CEILINGS[role] as readonly PermissionKey[]).includes(permissionKey);
}

/**
 * Whether the viewer may change this member's permissions. The same three conditions the
 * database applies (private.can_manage_member_permissions), so the screen never offers a
 * switch the save would refuse.
 */
export function canChangeMemberPermissions({
  viewerRole,
  viewerUserId,
  viewerHoldsPermissionsManage,
  targetRole,
  targetUserId,
  targetManagerUserId,
}: {
  viewerRole: OrgRole | null | undefined;
  viewerUserId: string | null | undefined;
  viewerHoldsPermissionsManage: boolean;
  targetRole: OrgRole | null | undefined;
  targetUserId: string | null | undefined;
  targetManagerUserId: string | null | undefined;
}): boolean {
  if (!viewerUserId || viewerUserId === targetUserId) return false;
  if (!outranks(viewerRole, targetRole) || !viewerHoldsPermissionsManage) return false;
  // A manager reaches only their own team.
  if (viewerRole === 'MANAGER') return targetManagerUserId === viewerUserId;
  return true;
}

/** Strictly above: nobody edits a peer, and nobody edits themselves. */
export function outranks(
  actorRole: OrgRole | null | undefined,
  targetRole: OrgRole | null | undefined,
): boolean {
  if (!actorRole || !targetRole) return false;
  return ROLE_RANK[actorRole] > ROLE_RANK[targetRole];
}

export function isPermissionKey(value: string): value is PermissionKey {
  return ALL_PERMISSION_KEYS.includes(value as PermissionKey);
}

export function roleAllowsPermission(role: OrgRole | null | undefined, permissionKey: PermissionKey): boolean {
  if (!role) return false;
  const defaults = ROLE_DEFAULT_PERMISSION_KEYS[role] as readonly PermissionKey[];
  return defaults.includes(permissionKey);
}

export function resolvePermission(
  role: OrgRole | null | undefined,
  permissionKey: PermissionKey,
  overrideEffect?: PermissionEffect | null,
): PermissionResolution {
  const defaultAllowed = roleAllowsPermission(role, permissionKey);

  if (role === 'BO') {
    return { allowed: true, defaultAllowed: true, source: 'owner' };
  }

  // A grant above the role's ceiling counts for nothing; the database ignores it too.
  if (overrideEffect === 'GRANT' && roleCeilingAllows(role, permissionKey)) {
    return { allowed: true, defaultAllowed, source: 'granted' };
  }

  if (overrideEffect === 'DENY') {
    return { allowed: false, defaultAllowed, source: 'denied' };
  }

  return { allowed: defaultAllowed, defaultAllowed, source: 'default' };
}

export function buildOverrideMap(overrides: PermissionOverrideLike[] | null | undefined) {
  const map = new Map<PermissionKey, PermissionEffect>();

  for (const override of overrides ?? []) {
    if (isPermissionKey(override.permission_key)) {
      map.set(override.permission_key, override.effect);
    }
  }

  return map;
}

export function buildEffectivePermissionMap(
  role: OrgRole | null | undefined,
  overrides: PermissionOverrideLike[] | null | undefined,
) {
  const overrideMap = buildOverrideMap(overrides);
  const map = new Map<PermissionKey, PermissionResolution>();

  for (const permissionKey of ALL_PERMISSION_KEYS) {
    map.set(permissionKey, resolvePermission(role, permissionKey, overrideMap.get(permissionKey)));
  }

  const reportsView = map.get('reports.view');
  const customReports = map.get('custom_reports.view');
  if (reportsView && customReports && !reportsView.allowed) {
    map.set('custom_reports.view', { ...customReports, allowed: false });
  }

  return map;
}
